import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { chatsApi } from "@/lib/humhub-api/chats";
import type { ChatRoomResponse, ChatRoomType } from "@/lib/humhub-api/types";
import { chatKeys } from "@/lib/queryKeys/chatKeys";

export interface ChatRoomsSectionState {
  accumulatedRooms: ChatRoomResponse[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  isLoadingMore: boolean;
  refresh: () => Promise<void>;
  totalUnreadCounts?: { unreadMessages: number; unreadMentions: number };
}

export const useChatRoomsSection = (
  type: ChatRoomType,
  perPage: number,
  withUnreadTotals = false,
): ChatRoomsSectionState => {
  const [refreshKey, setRefreshKey] = useState(0);

  const roomsQuery = useInfiniteQuery({
    queryKey: chatKeys.roomsInfinite({ perPage, type, refreshKey }),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => chatsApi.getChatRooms(pageParam, perPage, type),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    staleTime: 1 * 60 * 1000,
  });

  const { data: totalUnreadCounts } = useQuery({
    queryKey: chatKeys.unreadTotalsByType(type),
    queryFn: () => chatsApi.getChatUnreadCounts(type),
    staleTime: 1 * 60 * 1000,
    enabled: withUnreadTotals,
  });

  const accumulatedRooms = useMemo(() => {
    const rooms = roomsQuery.data?.pages.flatMap((page) => page.results) ?? [];
    const byId = new Map<number, ChatRoomResponse>();
    for (const room of rooms) {
      byId.set(room.id, room);
    }
    return Array.from(byId.values());
  }, [roomsQuery.data]);

  const loadMore = async () => {
    if (!roomsQuery.hasNextPage || roomsQuery.isFetchingNextPage) {
      return;
    }
    await roomsQuery.fetchNextPage();
  };

  const hasMore = roomsQuery.hasNextPage ?? false;

  const refresh = useCallback(async () => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return {
    accumulatedRooms,
    isLoading: roomsQuery.isLoading,
    error: roomsQuery.error ? String(roomsQuery.error) : null,
    hasMore,
    loadMore,
    isLoadingMore: roomsQuery.isFetchingNextPage,
    refresh,
    totalUnreadCounts,
  };
};
